import React from "react";
import { UseAppDispatch } from "redux/store";

import {
  Box,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";

import { goToTraveler, ITraveler } from "redux/travelerInformation";
import { IContactInformation } from "redux/contactInformation";
import { goToStep } from "redux/checkoutSteps";
import { setInTransition } from "redux/transition/transition.slice";
import Edit from "@mui/icons-material/Edit";
import { datestringToBirthdate, useConfig } from "@thekilroygroup/shared-ui";

interface IProps {
  contactPerson?: IContactInformation;
  traveler?: ITraveler;
  title: string;
  index?: number;
}

export default function TravelerInformationBox(props: IProps) {
  const { contactPerson, traveler, title, index } = props;
  const dispatch = UseAppDispatch();
  const theme = useTheme();

  const { locale } = useConfig();

  const handleContactEditClick = () => {
    dispatch(setInTransition(true));

    setTimeout(() => {
      dispatch(goToStep(1));
      window.scrollTo(0, 0);
      dispatch(setInTransition(false));
    }, theme.transitions.duration.standard);
  };

  const handleTravelerEditClick = () => {
    if (index != undefined) {
      dispatch(setInTransition(true));

      setTimeout(() => {
        dispatch(goToTraveler(index));
        window.scrollTo(0, 0);
        dispatch(setInTransition(false));
      }, theme.transitions.duration.standard);
    }
  };

  return (
    <>
      {contactPerson && (
        <Box sx={sxStyles.wrapper}>
          <Stack flexWrap="wrap" direction="row" justifyContent="space-between">
            <Typography variant="h5" sx={sxStyles.title}>
              {title}
            </Typography>

            <IconButton
              sx={sxStyles.editButton}
              color="primary"
              aria-label="edit"
              onClick={handleContactEditClick}
            >
              <Edit />
            </IconButton>
          </Stack>

          <Stack flexWrap="wrap" direction="row">
            <Typography sx={sxStyles.column}>
              {contactPerson.firstName} {contactPerson.middleName}{" "}
              {contactPerson.lastName}
            </Typography>
            <Typography sx={sxStyles.column}>
              {contactPerson.genderType}
            </Typography>
          </Stack>
          <Stack flexWrap="wrap" justifyContent="space-between">
            <Typography sx={sxStyles.column}>{contactPerson.email}</Typography>
            <Typography sx={sxStyles.column}>
              {contactPerson.phoneNumber?.international}
            </Typography>
          </Stack>
        </Box>
      )}
      {traveler && (
        <Box sx={sxStyles.wrapper}>
          <Stack flexWrap="wrap" justifyContent="space-between" direction="row">
            <Typography variant="h5" sx={sxStyles.title}>
              {title}
            </Typography>

            <IconButton
              sx={sxStyles.editButton}
              color="primary"
              aria-label="edit"
              onClick={handleTravelerEditClick}
            >
              <Edit />
            </IconButton>
          </Stack>
          <Stack flexWrap="wrap" direction="row" justifyContent="space-between">
            <Typography sx={sxStyles.column}>
              {traveler.formFields.firstName} {traveler.formFields.middleName}{" "}
              {traveler.formFields.lastName}
            </Typography>
            <Typography sx={sxStyles.column}>
              {traveler.formFields.genderType}
            </Typography>
          </Stack>
          <Stack flexWrap="wrap" direction="row" justifyContent="space-between">
            {traveler.formFields.dateOfBirth ? (
              <Typography sx={sxStyles.column}>
                {datestringToBirthdate(traveler.formFields.dateOfBirth, locale)}
              </Typography>
            ) : (
              <Typography variant="titleSmall" sx={{ color: "error.main" }}>
                {traveler.formFields.dateOfBirth}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  wrapper: {
    p: 2,
    borderBottom: "1px solid ",
    borderBottomColor: "grey.300",
  },
  title: {
    color: "primary.main",
    mb: 2,
  },
  column: {
    maxWidth: "100%",
  },
  editButton: {
    mr: -1.5,
  },
};
